import React, { FC, useEffect, useState } from 'react';
import { Table } from 'Shared/components';
import { useLazyApi } from 'Shared/services/Api';
import classNames from 'classnames';

import {
  Asset,
  Button,
  FilePdfIcon,
  WarningIcon,
  PaperPlaneIcon,
  Loading,
  CheckmarkIcon,
  CachedIcon,
  InfoFilledIcon,
  ArrowForwardIcon,
} from '@clearcover/atlas';
import {
  CheckPacketsSignatureStatusResponseType,
  GetSignaturePacketResponseType,
  SignaturePacketType,
} from '@clearcover/core-app-types';

import styles from './styles.module.css';

import {
  DeliverSignableFormsType,
  SignatureRequiredFormType,
} from '../PolicySignaturesRequired';
import { LazytApiExecuteFuncType } from 'Shared/services/Api/makeHooks';
import { formatDateWithTime } from 'Shared/utils';
import {
  getStoredCurrentPacket,
  getStoredCurrentSignatureRequestId,
  storeCurrentPacket,
  storeCurrentSignatureRequestId,
} from './utils';

const CANCELLED = 'CANCELLED';
const COMPLETE = 'COMPLETE';
const SENT = 'SENT';

export const docuSignBaseLink =
  'https://app.hellosign.com/editor/view/super_group_guid/';

export const PacketSignatureSection: FC<PacketSignatureSectionPropsType> = ({
  forms,
  policyId,
  policyHolder,
  draftId,
  timeZone,
  setPacketsHaveBeenSigned,
}) => {
  const [
    {
      data: getSignaturePacketResponse,
      loading: loadingCurrentPackets,
      error: getSignaturePacketError,
    },
    getCurrentPacket,
  ] = useLazyApi<GetSignaturePacketResponseType>({
    url: `/policies/${policyId}/signaturePackets`,
  });
  const [
    {
      data: deliverResult,
      loading: loadingDeliverSignableForms,
      error: deliverError,
    },
    deliverSignableForms,
  ] = useLazyApi<DeliverSignableFormsType>({
    url: `/policies/${policyId}/drafts/${draftId}/deliverSignableForms`,
    method: 'POST',
  });
  const [
    {
      data: checkSignatureResponse,
      loading: checkingSignatureStatus,
      error: checkSignatureError,
    },
    checkSignatureStatus,
  ] = useLazyApi<CheckPacketsSignatureStatusResponseType>({
    url: `/policies/${policyId}/checkSignatureStatus`,
  });

  const [currentPacket, setCurrentPacket] =
    useState<SignaturePacketType | null>(
      getStoredCurrentPacket(policyId, draftId)
    );

  useEffect(() => {
    if (getSignaturePacketResponse?.packet) {
      storeCurrentPacket(policyId, draftId, getSignaturePacketResponse.packet);
      setCurrentPacket(getSignaturePacketResponse.packet);
    }
  }, [getSignaturePacketResponse, policyId, draftId]);

  useEffect(() => {
    if (deliverResult?.signatureRequestId) {
      getCurrentPacket();
      storeCurrentSignatureRequestId(
        policyId,
        draftId,
        deliverResult.signatureRequestId
      );
    } else if (getStoredCurrentSignatureRequestId(policyId, draftId)) {
      getCurrentPacket();
    }
  }, [deliverResult, policyId, draftId, getCurrentPacket]);

  useEffect(() => {
    if (checkSignatureResponse?.packetsHaveBeenSigned) {
      setPacketsHaveBeenSigned();
    }
    if (
      checkSignatureResponse?.packets.some(
        (packet) => packet.status === CANCELLED
      )
    ) {
      getCurrentPacket();
    }
  }, [checkSignatureResponse, setPacketsHaveBeenSigned, getCurrentPacket]);

  const isLoading =
    loadingDeliverSignableForms ||
    loadingCurrentPackets ||
    loadingDeliverSignableForms;

  return (
    <>
      <Table
        className={styles.table}
        columnWidths={['35px', 10]}
        striped={false}
        rows={forms.map(({ name }, index) => {
          return {
            key: index,
            data: [
              <Asset
                icon={FilePdfIcon}
                height={16}
                width={16}
                description="Pdf Icon"
              />,
              name,
            ],
          };
        })}
      />
      {!checkSignatureResponse?.packetsHaveBeenSigned && (
        <SendAndCheckPacketsBar
          currentPacket={currentPacket}
          packetSent={
            !!deliverResult?.signatureRequestId ||
            getStoredCurrentSignatureRequestId(policyId, draftId) !== '' ||
            getSignaturePacketResponse?.packet?.status === SENT ||
            getStoredCurrentPacket(policyId, draftId)?.status === SENT
          }
          policyHolder={policyHolder}
          loadingDeliverSignableForms={loadingDeliverSignableForms}
          deliverSignableForms={deliverSignableForms}
          checkSignatureStatus={() => {
            if (currentPacket) {
              checkSignatureStatus({
                method: 'POST',
                data: { packetIds: [currentPacket?.id] },
              });
            } else {
              getCurrentPacket();
            }
          }}
          checkingSignatureStatus={checkingSignatureStatus}
          timeZone={timeZone}
        />
      )}
      {checkSignatureResponse?.packetsHaveBeenSigned && (
        <SignatureReceived
          deliverResultSignatureId={
            deliverResult?.signatureRequestId ??
            getStoredCurrentSignatureRequestId(policyId, draftId)
          }
          checkSignatureResponse={checkSignatureResponse}
          timeZone={timeZone}
          policyHolder={policyHolder}
        />
      )}
      <div
        className={classNames(
          {
            [styles.loadingSection]: isLoading,
          },
          styles.errorSection
        )}
      >
        {isLoading && <Loading data-testid={'loading-spinner'} />}
        {(deliverError || checkSignatureError || getSignaturePacketError) && (
          <>
            <Asset
              icon={WarningIcon}
              height={16}
              width={16}
              description="Error Icon"
            />
            <span>
              Our systems are having trouble communicating right now - please
              try again later
            </span>
          </>
        )}
      </div>
    </>
  );
};

export type PacketSignatureSectionPropsType = {
  forms: Array<SignatureRequiredFormType>;
  policyId: string;
  draftId: string;
  policyHolder: { name: string; email: string };
  canViewDocuments: boolean;
  timeZone: string;
  setPacketsHaveBeenSigned: () => void;
};

const SendAndCheckPacketsBar: FC<{
  currentPacket: SignaturePacketType | null;
  packetSent: boolean;
  policyHolder: { name: string; email: string };
  loadingDeliverSignableForms: boolean;
  deliverSignableForms: LazytApiExecuteFuncType<DeliverSignableFormsType>;
  checkSignatureStatus: () => void;
  checkingSignatureStatus: boolean;
  timeZone: string;
}> = ({
  currentPacket,
  policyHolder,
  packetSent,
  loadingDeliverSignableForms,
  deliverSignableForms,
  checkSignatureStatus,
  checkingSignatureStatus,
  timeZone,
}) => {
  return (
    <div
      className={classNames(styles.sendSignatureBar, {
        [styles.packetSent]: packetSent,
      })}
    >
      <div className={styles.iconContainer}>
        <Asset
          icon={packetSent ? InfoFilledIcon : WarningIcon}
          height={16}
          width={16}
          description="Send Info Icon"
        />
      </div>
      <div className={styles.textContainer}>
        <span className={styles.infomationalText}>
          Policy owner must provide an electronic signature confirming this
          change.
        </span>
        {packetSent ? (
          <span>
            Email sent to <u>{policyHolder.email}</u>{' '}
            {currentPacket && (
              <>on {formatDateWithTime(currentPacket?.updatedAt, timeZone)}</>
            )}
          </span>
        ) : (
          <span>
            Send email to <u>{policyHolder.email}</u>
          </span>
        )}
        <div className={styles.buttonContainer}>
          <Button
            className={styles.sendSignatureBarButton}
            data-testid="send-signature"
            variant="secondary"
            size="sm"
            onClick={() => {
              deliverSignableForms();
            }}
            disabled={loadingDeliverSignableForms}
          >
            <Asset
              icon={PaperPlaneIcon}
              height={16}
              width={16}
              description="Paper plane icon"
            />
            {packetSent
              ? 'Resend E-Signature Packet'
              : 'Send E-Signature Packet'}
          </Button>
          <Button
            className={styles.sendSignatureBarButton}
            data-testid="check-signature-status"
            variant="secondary"
            size="sm"
            onClick={() => {
              checkSignatureStatus();
            }}
            isSubmitting={checkingSignatureStatus}
            disabled={!packetSent || checkingSignatureStatus}
          >
            <Asset
              icon={CachedIcon}
              height={16}
              width={16}
              description="Cached Icon"
            />
            Check Signature Status
          </Button>
        </div>
      </div>
    </div>
  );
};
const SignatureReceived: FC<{
  deliverResultSignatureId: string;
  checkSignatureResponse: CheckPacketsSignatureStatusResponseType;
  timeZone: string;
  policyHolder: { name: string; email: string };
}> = ({
  deliverResultSignatureId,
  timeZone,
  policyHolder,
  checkSignatureResponse,
}) => {
  return (
    <div
      data-testid="signature-complete"
      className={classNames(styles.sendSignatureBar, styles.signatureComplete)}
    >
      <div className={styles.iconContainer}>
        <Asset
          icon={CheckmarkIcon}
          height={16}
          width={16}
          description="Checkmark icon"
        />
      </div>
      <div className={styles.textContainer}>
        <span>Policy owner has signed the documents.</span>
        <span>
          Signed on{' '}
          {formatDateWithTime(
            checkSignatureResponse.packets.find(
              (packet) => packet.status === COMPLETE
            )?.completedAt || '',
            timeZone
          )}{' '}
          by {policyHolder.name}
        </span>
        <div className={styles.buttonContainer}>
          <a
            data-testid={'external-check-signature-status-link'}
            target={'_blank'}
            rel={'noreferrer'}
            aria-disabled={deliverResultSignatureId ? 'false' : 'true'}
            href={
              deliverResultSignatureId
                ? `${docuSignBaseLink}${deliverResultSignatureId}`
                : ''
            }
          >
            <Button
              className={styles.sendSignatureBarButton}
              data-testid="external-check-signature-status"
              disabled={!deliverResultSignatureId}
              variant="secondary"
              size="sm"
            >
              Check Signature Status
              <Asset
                icon={ArrowForwardIcon}
                height={16}
                width={16}
                description="Arrow Forward icon"
              />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};
